// src/theme.js
import { createTheme } from '@mui/material/styles';

const baseThemeOptions = {
  palette: {
    primary: {
      main: '#1F6FEB', // Edge Blue
      light: '#EAF2FE', // Blue Tint
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#0B2545', // Vault Navy
    },
    error: { main: '#D6323A' }, // Critical
    warning: { main: '#F0A202' }, // Elevated
    info: { main: '#E2622B' }, // High
    success: { main: '#00A88F' }, // Secure
    background: {
      default: '#FFFFFF', // Pure White
      paper: '#FFFFFF', // Pure White
    },
    text: {
      primary: '#0B2545', // Vault Navy
      secondary: '#6B7A90', // Slate
    },
    divider: '#E4E9F0', // Hairline
    action: {
      hover: '#EAF2FE',
      disabledBackground: '#F7F9FC', // Mist
      disabled: '#6B7A90',
    },
  },
  typography: {
    fontFamily: '"Calibri", "Arial", sans-serif',
    h1: {
      fontFamily: '"Arial", sans-serif',
      fontWeight: 'bold',
      fontSize: '48px',
      lineHeight: 1.1,
      color: 'var(--vault-navy)',
    },
    h2: {
      fontFamily: '"Arial", sans-serif',
      fontWeight: 'bold',
      fontSize: '32px',
      lineHeight: 1.2,
      color: 'var(--vault-navy)',
    },
    h3: {
      fontFamily: '"Arial", sans-serif',
      fontWeight: 'bold',
      fontSize: '20px',
      lineHeight: 1.3,
      color: 'var(--vault-navy)',
    },
    overline: {
      fontFamily: '"Arial", sans-serif',
      fontWeight: 'bold',
      fontSize: '12px',
      textTransform: 'uppercase',
      letterSpacing: '0.1em',
    },
    body1: {
      fontFamily: '"Calibri", sans-serif',
      fontSize: '17px',
      lineHeight: 1.6,
    },
    body2: {
      fontFamily: '"Calibri", sans-serif',
      fontSize: '14px',
    },
    caption: {
      fontFamily: '"Calibri", sans-serif',
      fontSize: '14px',
      color: 'var(--slate)',
    },
    button: {
      fontFamily: '"Arial", sans-serif',
      fontWeight: 'bold',
      fontSize: '16px',
      textTransform: 'none',
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          color: 'var(--vault-navy)',
          backgroundColor: 'var(--canvas)',
        }
      }
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          color: 'var(--vault-navy)',
        }
      }
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: 'var(--canvas)',
          color: 'var(--vault-navy)',
        }
      }
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          color: 'var(--vault-navy)',
          '&:hover': {
            backgroundColor: 'var(--mist) !important',
          },
          '&.Mui-selected': {
            backgroundColor: 'var(--blue-tint) !important',
          },
          '&.Mui-selected:hover': {
            backgroundColor: 'var(--blue-tint) !important',
          }
        }
      }
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          color: 'var(--vault-navy)',
        }
      }
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '6px',
          padding: '10px 20px',
          boxShadow: 'none',
        },
        containedPrimary: {
          backgroundColor: '#1F6FEB',
          color: 'var(--canvas)',
          '&:hover': {
            backgroundColor: 'var(--blue-tint)',
            // color: '#1F6FEB',
            boxShadow: 'none',
          },
        },
        outlinedPrimary: {
          borderColor: 'var(--hairline)',
          backgroundColor: 'var(--canvas)',
          color: 'var(--vault-navy)',
          border: '1px solid #E4E9F0',
          '&:hover': {
            backgroundColor: 'var(--blue-tint)',
            borderColor: 'var(--hairline)',
          }
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: 'var(--canvas)',
          border: '1px solid var(--hairline)',
          borderRadius: '8px',
          boxShadow: 'none',
        }
      }
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          fontFamily: '"Arial", sans-serif',
          fontWeight: 'bold',
          fontSize: '12px',
          textTransform: 'uppercase',
          color: 'var(--slate)',
          backgroundColor: 'var(--mist)',
          borderBottom: '1px solid var(--hairline)',
        },
        body: {
          fontFamily: '"Calibri", sans-serif',
          borderBottom: '1px solid var(--hairline)',
        }
      }
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: 'var(--blue-tint) !important',
          },
          backgroundColor: 'var(--mist)',
          '&.Mui-selected, &.Mui-selected:hover': {
            backgroundColor: 'var(--blue-tint)',
          }
        }
      }
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: '6px',
          backgroundColor: 'var(--canvas)',
          border: '1px solid var(--hairline)',
          fontFamily: '"Arial", sans-serif',
          fontWeight: 'bold',
          fontSize: '12px',
          color: 'var(--ink, #000000)',
          '&.MuiChip-filled': {
            color: 'var(--ink, #000000)',
          },
          '& .MuiChip-label': {
            color: 'inherit',
          },
        },
      }
    },
    MuiTablePagination: {
      styleOverrides: {
        root: {
          color: 'var(--slate)',
        },
        selectIcon: {
          color: 'var(--slate)',
        },
        actions: {
          color: 'var(--slate)',
        }
      }
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          color: 'var(--slate)',
        }
      }
    }
  }
};

export const lightTheme = createTheme({
  ...baseThemeOptions,
  palette: {
    ...baseThemeOptions.palette,
    mode: 'light',
  },
});

// We enforce the same palette for "dark mode" as well to comply with "No Full Dark Mode Surfaces" rule
export const darkTheme = createTheme({
  ...baseThemeOptions,
  palette: {
    ...baseThemeOptions.palette,
    mode: 'light', // Keep light mode rendering for MUI internals
  },
});
